import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, API } from "../lib/api";
import { contactEmail, brandName } from "../lib/domain";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Mail, Send, Info, ExternalLink, AlertTriangle, Inbox, PenSquare, FolderClosed,
  FolderPlus, Upload, FileText, Trash2, ArrowLeft, RefreshCw, Lock, Paperclip, Sparkles, Pencil, Eye, Newspaper,
  QrCode, Image as ImageIcon, Link2, MessageCircle, Facebook,
} from "lucide-react";

const fmtDate = (s) => {
  try { return new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

import { isNoldDomain } from "../lib/domain";

const REPLY_TEMPLATES_BENTSKIN = [
  {
    id: "accueil", label: "Accueil",
    subject: "Bienvenue chez BentSkin Go",
    body: "Bonjour,\n\nMerci d'avoir contacté BentSkin Go ! Nous sommes ravis de vous compter parmi nous. Notre équipe reste à votre disposition pour toute question concernant nos courses moto et nos services.\n\nÀ très bientôt sur la route !",
  },
  {
    id: "reclamation", label: "Réclamation",
    subject: "Suivi de votre réclamation",
    body: "Bonjour,\n\nNous avons bien reçu votre réclamation et nous en sommes sincèrement désolés. Votre satisfaction est notre priorité : notre équipe examine votre dossier et reviendra vers vous dans les plus brefs délais avec une solution.\n\nMerci de votre patience et de votre confiance.",
  },
  {
    id: "tarif", label: "Tarif",
    subject: "Nos tarifs BentSkin Go",
    body: "Bonjour,\n\nMerci pour votre intérêt. Nos tarifs dépendent de la distance et de la ville. Le montant estimé s'affiche toujours avant la confirmation de votre course dans l'application, sans frais cachés. Paiement possible en espèces, Orange Money ou MTN Mobile Money.\n\nN'hésitez pas si vous avez d'autres questions.",
  },
];

const REPLY_TEMPLATES_NOLD = [
  {
    id: "accueil", label: "Accueil",
    subject: "Bienvenue chez Nold Go",
    body: "Bonjour,\n\nMerci d'avoir contacté Nold Go ! Nous sommes ravis de vous compter parmi nous. Notre équipe reste à votre disposition pour toute question concernant nos trajets VTC et notre service de livraison de repas à Bruxelles.\n\nÀ très bientôt sur la route !",
  },
  {
    id: "reclamation", label: "Réclamation",
    subject: "Suivi de votre réclamation",
    body: "Bonjour,\n\nNous avons bien reçu votre réclamation et nous en sommes sincèrement désolés. Votre satisfaction est notre priorité : notre équipe examine votre dossier et reviendra vers vous dans les plus brefs délais avec une solution.\n\nMerci de votre patience et de votre confiance.",
  },
  {
    id: "tarif", label: "Tarif",
    subject: "Nos tarifs Nold Go",
    body: "Bonjour,\n\nMerci pour votre intérêt. Nos tarifs dépendent de la distance et de la classe de véhicule (Éco, Confort ou Livraison vélo). Le montant estimé s'affiche toujours avant la confirmation de votre course dans l'application, sans frais cachés. Paiement sécurisé par carte bancaire via Stripe.\n\nN'hésitez pas si vous avez d'autres questions.",
  },
];

const REPLY_TEMPLATES = isNoldDomain() ? REPLY_TEMPLATES_NOLD : REPLY_TEMPLATES_BENTSKIN;

export function MailConsole() {
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("messages");
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    api.get("/admin/mail/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  const _isNold = isNoldDomain();
  const tabs = _isNold ? [
    { id: "messages", label: "Messages reçus", icon: Inbox },
    { id: "compose", label: "Écrire", icon: PenSquare },
    { id: "documents", label: "Documents", icon: FolderClosed },
  ] : [
    { id: "messages", label: "Messages reçus", icon: Inbox },
    { id: "compose", label: "Écrire", icon: PenSquare },
    { id: "flyers", label: "Flyers", icon: QrCode },
    { id: "documents", label: "Documents", icon: FolderClosed },
    { id: "actus", label: "Actus défilantes", icon: Newspaper },
  ];

  return (
    <div data-testid="mail-console">
      <div className="mb-4">
        <h2 className="font-head font-black text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-[#F26419]" /> Espace Direction</h2>
        <p className="text-sm text-[#75807A]">{status?.mailbox || contactEmail()}</p>
      </div>

      <div className="flex gap-2 mb-5 border-b border-[#E2DFD8]">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            data-testid={`mail-tab-${tb.id}`}
            onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === tb.id ? "border-[#F26419] text-[#F26419]" : "border-transparent text-[#75807A] hover:text-[#1A1A1A]"
            }`}
          >
            <tb.icon className="w-4 h-4" /> {tb.label}
          </button>
        ))}
      </div>

      {tab === "messages" && <MessagesTab onReply={(to) => { setPrefill({ to, ts: Date.now() }); setTab("compose"); }} />}
      {tab === "compose" && <ComposeTab status={status} prefill={prefill} />}
      {tab === "flyers" && <FlyersTab />}
      {tab === "documents" && <DocumentsTab />}
      {tab === "actus" && <ActusTab />}
    </div>
  );
}

/* ---------------- Flyers (supports digitaux à partager) ---------------- */
function FlyersTab() {
  const items = [
    { v: "chauffeur", title: "Flyer CHAUFFEUR", tag: "Partenaires", desc: "Recrutement — 0% commission pendant 3 mois, puis 20%." },
    { v: "client", title: "Flyer CLIENT", tag: "Passagers", desc: "Acquisition — commandez votre course en 1 scan." },
  ];
  const jpg = (v) => `${API}/marketing/flyer-side.jpg?v=${v}`;
  const pdf = (v) => `${API}/marketing/flyer-side.pdf?v=${v}`;
  const dl = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = name; a.target = "_blank"; a.click(); };
  const copyLink = async (v) => {
    try { await navigator.clipboard.writeText(jpg(v)); toast.success("Lien image copié — collez-le sur TikTok / Instagram ✅"); }
    catch { toast.error("Copie impossible"); }
  };
  const copyText = async (txt, ok) => {
    try { await navigator.clipboard.writeText(txt); toast.success(ok); }
    catch { toast.error("Copie impossible"); }
  };
  const waText = "Découvrez BentSkin Go 🛵 — vos trajets en toute confiance. Scannez et commandez : ";
  return (
    <div data-testid="flyers-tab">
      <div className="rounded-2xl border border-[#F26419]/25 bg-[#FEF4EC] p-3 mb-4 flex items-start gap-2 text-sm text-[#3B423E]">
        <Info className="w-4 h-4 text-[#F26419] mt-0.5 shrink-0" />
        <span>Deux versions prêtes à diffuser : <b>PDF</b> (mail aux partenaires) et <b>JPG haute qualité</b> (WhatsApp, Facebook, TikTok/Instagram via « copier le lien »).</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.v} data-testid={`flyer-card-${it.v}`} className="rounded-2xl border border-[#E2DFD8] bg-white overflow-hidden">
            <img src={jpg(it.v)} alt={it.title} data-testid={`flyer-preview-${it.v}`} className="w-full h-auto block bg-[#FDF8F0]" />
            <div className="p-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FEF1E7] text-[#D94F2B]">{it.tag}</span>
                <h4 className="font-head font-bold text-[#1A1A1A]">{it.title}</h4>
              </div>
              <p className="mt-1 text-sm text-[#75807A]">{it.desc}</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button data-testid={`flyer-pdf-${it.v}`} onClick={() => dl(pdf(it.v), `bentskin-go-flyer-${it.v}.pdf`)} className="h-10 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm inline-flex items-center justify-center gap-2"><FileText className="w-4 h-4" /> PDF</button>
                <button data-testid={`flyer-jpg-${it.v}`} onClick={() => dl(jpg(it.v), `bentskin-go-flyer-${it.v}.jpg`)} className="h-10 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm inline-flex items-center justify-center gap-2"><ImageIcon className="w-4 h-4" /> JPG HD</button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <a data-testid={`flyer-wa-${it.v}`} href={`https://wa.me/?text=${encodeURIComponent(waText + jpg(it.v))}`} target="_blank" rel="noreferrer" className="flex-1 h-10 rounded-xl bg-[#25D366] hover:opacity-90 text-white font-bold text-sm inline-flex items-center justify-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                <a data-testid={`flyer-fb-${it.v}`} href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jpg(it.v))}`} target="_blank" rel="noreferrer" className="flex-1 h-10 rounded-xl bg-[#1877F2] hover:opacity-90 text-white font-bold text-sm inline-flex items-center justify-center gap-2"><Facebook className="w-4 h-4" /> Facebook</a>
                <button data-testid={`flyer-copy-${it.v}`} onClick={() => copyLink(it.v)} title="Copier le lien (TikTok / Instagram)" className="h-10 px-3 rounded-xl border border-[#E2DFD8] hover:border-[#F26419] text-[#3B423E]"><Link2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3 className="font-head font-bold text-[#1A1A1A] mt-8 mb-1 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-[#F26419]" /> Pack réseaux sociaux</h3>
      <p className="text-sm text-[#75807A] mb-4">Formats prêts à poster : <b>carré 1080×1080</b> (posts Facebook/Instagram) et <b>story 1080×1920</b> (WhatsApp/TikTok/Reels).</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { v: "chauffeur", f: "square", label: "Chauffeur · Carré" },
          { v: "chauffeur", f: "story", label: "Chauffeur · Story" },
          { v: "client", f: "square", label: "Client · Carré" },
          { v: "client", f: "story", label: "Client · Story" },
        ].map((it) => {
          const url = `${API}/marketing/social.jpg?v=${it.v}&format=${it.f}`;
          return (
            <div key={`${it.v}-${it.f}`} data-testid={`social-tile-${it.v}-${it.f}`} className="rounded-2xl border border-[#E2DFD8] bg-white overflow-hidden flex flex-col">
              <div className={`bg-[#FDF8F0] flex items-center justify-center ${it.f === "story" ? "h-56" : "h-40"}`}>
                <img src={url} alt={it.label} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="text-sm font-bold text-[#1A1A1A]">{it.label}</div>
                <div className="mt-auto flex items-center gap-2">
                  <button data-testid={`social-dl-${it.v}-${it.f}`} onClick={() => dl(url, `bentskin-go-${it.v}-${it.f}.jpg`)} className="flex-1 h-9 rounded-lg bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-xs inline-flex items-center justify-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Télécharger</button>
                  <a data-testid={`social-wa-${it.v}-${it.f}`} href={`https://wa.me/?text=${encodeURIComponent(waText + url)}`} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-lg bg-[#25D366] hover:opacity-90 text-white inline-flex items-center justify-center"><MessageCircle className="w-4 h-4" /></a>
                  <button data-testid={`social-copy-${it.v}-${it.f}`} onClick={async () => { try { await navigator.clipboard.writeText(url); toast.success("Lien copié ✅"); } catch { toast.error("Copie impossible"); } }} className="h-9 px-3 rounded-lg border border-[#E2DFD8] hover:border-[#F26419] text-[#3B423E]"><Link2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="font-head font-bold text-[#1A1A1A] mt-8 mb-1 flex items-center gap-2"><Newspaper className="w-5 h-5 text-[#F26419]" /> Calendrier de lancement · 7 posts</h3>
      <p className="text-sm text-[#75807A] mb-4">Un post prêt-à-publier par jour (visuel + légende + hashtags). Copiez la légende, téléchargez le visuel, publiez !</p>
      <div className="space-y-3">
        {CALENDAR.map((c) => {
          const url = `${API}/marketing/post.jpg?n=${c.n}`;
          return (
            <div key={c.n} data-testid={`post-card-${c.n}`} className="rounded-2xl border border-[#E2DFD8] bg-white overflow-hidden flex flex-col sm:flex-row">
              <div className="sm:w-40 shrink-0 bg-[#FDF8F0] flex items-center justify-center p-2">
                <img src={url} alt={c.day} className="max-h-40 w-auto object-contain rounded-lg" />
              </div>
              <div className="p-4 flex-1 min-w-0">
                <div className="text-xs font-bold text-[#D94F2B]">{c.day}</div>
                <p className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap">{c.caption}</p>
                <p className="mt-2 text-xs text-[#5570C8] break-words">{c.tags}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button data-testid={`post-dl-${c.n}`} onClick={() => dl(url, `bentskin-go-post-${c.n}.jpg`)} className="h-9 px-3 rounded-lg bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-xs inline-flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Visuel</button>
                  <button data-testid={`post-copy-cap-${c.n}`} onClick={() => copyText(c.caption, "Légende copiée ✅")} className="h-9 px-3 rounded-lg border border-[#E2DFD8] hover:border-[#F26419] text-[#3B423E] text-xs font-semibold inline-flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Copier la légende</button>
                  <button data-testid={`post-copy-tags-${c.n}`} onClick={() => copyText(c.tags, "Hashtags copiés ✅")} className="h-9 px-3 rounded-lg border border-[#E2DFD8] hover:border-[#F26419] text-[#3B423E] text-xs font-semibold inline-flex items-center gap-1.5">#️⃣ Hashtags</button>
                  <a data-testid={`post-wa-${c.n}`} href={`https://wa.me/?text=${encodeURIComponent(c.caption + " " + c.tags + " " + url)}`} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-lg bg-[#25D366] hover:opacity-90 text-white font-bold text-xs inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CALENDAR = [
  { n: 1, day: "Jour 1 · J-3 (Teaser)", caption: "🛵 BentSkin Go arrive bientôt dans votre ville ! Des courses simples, rapides et au juste prix. Restez connectés… 📲", tags: "#BentSkinGo #Moto #Cameroun #Bientôt #Mobilité" },
  { n: 2, day: "Jour 2 · J-1 (Compte à rebours)", caption: "⏳ Plus qu'un jour ! Demain, commandez votre course en un seul scan avec BentSkin Go. Prêts ? 🙌", tags: "#BentSkinGo #Lancement #Moto #Cameroun #JMoins1" },
  { n: 3, day: "Jour 3 · Jour J (Lancement)", caption: "🎉 C'est officiel : BentSkin Go est lancé ! Pour fêter ça, profitez de 3 MOIS DE COURSES GRATUITES. Scannez et roulez. 🛵💨", tags: "#BentSkinGo #Lancement #CoursesGratuites #Moto #Cameroun" },
  { n: 4, day: "Jour 4 · Offre passagers", caption: "🎁 3 mois de courses OFFERTES ! Téléchargez, scannez le QR, roulez. Offre de lancement limitée. 🚀", tags: "#BentSkinGo #Promo #Gratuit #VTC #Moto #Cameroun" },
  { n: 5, day: "Jour 5 · Recrutement chauffeurs", caption: "🛵 Deviens chauffeur BentSkin Go : 0% de commission pendant 3 mois, puis 20%. Indépendant, tu gères ton temps. Rejoins-nous ! 💪", tags: "#BentSkinGo #Emploi #Chauffeur #Moto #Cameroun #Job" },
  { n: 6, day: "Jour 6 · Sécurité & confiance", caption: "✅ Chauffeurs vérifiés, prix affiché à l'avance, suivi du trajet en direct. Avec BentSkin Go, voyagez l'esprit tranquille. 🙏", tags: "#BentSkinGo #Sécurité #Confiance #Moto #Cameroun" },
  { n: 7, day: "Jour 7 · Parrainage", caption: "🤝 Parrainez vos amis sur BentSkin Go et gagnez des bonus ! Plus on est nombreux, mieux on roule. 🎈", tags: "#BentSkinGo #Parrainage #Bonus #Communauté #Cameroun" },
];
function ActusTab() {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/ticker-news").then((r) => setText((r.data.items || []).join("\n"))).catch((e) => toast.error(apiError(e)));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const items = text.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/admin/ticker-news", { items });
      toast.success("Actus mises à jour ✅");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl" data-testid="actus-tab">
      <div className="rounded-2xl border border-[#F26419]/25 bg-[#FEF4EC] p-3 mb-4 flex items-start gap-2 text-sm text-[#3B423E]">
        <Info className="w-4 h-4 text-[#F26419] mt-0.5 shrink-0" />
        <span>Une ligne = un message qui défile dans le bandeau du site. Ils s'ajoutent à la <b>météo</b> et aux <b>actualités du Cameroun</b> (mises à jour automatiquement).</span>
      </div>
      <textarea data-testid="actus-textarea" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Un message par ligne…" className="w-full rounded-xl border-2 border-[#E2DFD8] px-4 py-3 focus:outline-none focus:border-[#F26419]" />
      <Button data-testid="actus-save" onClick={save} disabled={saving} className="mt-3 gap-2 bg-[#F26419] hover:bg-[#D85312] text-white">
        <Send className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </Button>
    </div>
  );
}

/* ---------------- Messages reçus (formulaire de contact) ---------------- */
function MessagesTab({ onReply }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/contact-messages"); setMsgs(r.data.messages || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = async (m) => {
    setSel(m);
    if (!m.read) {
      try { await api.post(`/admin/contact-messages/${m.id}/read`); setMsgs((p) => p.map((x) => x.id === m.id ? { ...x, read: true } : x)); } catch {}
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#75807A]">Messages envoyés via le formulaire « Contact support » du site.</p>
        <Button data-testid="messages-refresh" variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>
      <div className="rounded-2xl border border-[#F26419]/25 bg-[#FEF4EC] p-3 mb-4 flex items-start gap-2 text-sm text-[#3B423E]">
        <Info className="w-4 h-4 text-[#F26419] mt-0.5 shrink-0" />
        <span>Les emails classiques reçus se consultent sur le <a href={isNoldDomain() ? "https://email.noldgo.com" : "https://email.bentskingo.com"} target="_blank" rel="noreferrer" className="font-semibold text-[#F26419] hover:underline inline-flex items-center gap-1">webmail <ExternalLink className="w-3.5 h-3.5" /></a>. Ici s'affichent les messages du formulaire de contact.</span>
      </div>

      {sel ? (
        <div data-testid="message-detail" className="rounded-2xl border border-[#E2DFD8] bg-white p-5">
          <button onClick={() => setSel(null)} className="text-sm text-[#75807A] flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <div className="font-bold text-lg">{sel.name}</div>
          <div className="text-sm text-[#3B423E]">{sel.phone || "—"} {sel.email ? `· ${sel.email}` : ""}</div>
          <div className="text-xs text-[#75807A] mb-4">{fmtDate(sel.created_at)}</div>
          <div className="whitespace-pre-wrap text-[#1A1A1A]">{sel.message}</div>
          {sel.email && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="message-reply-btn" onClick={() => onReply?.(sel.email)} className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold">
                <Send className="w-4 h-4" /> Répondre depuis le dashboard
              </button>
              <a href={`mailto:${sel.email}`} className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-[#E2DFD8] text-[#3B423E] font-bold hover:border-[#F26419]">Mail classique</a>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-[#E2DFD8] bg-white overflow-hidden">
          {loading && msgs.length === 0 && <div className="p-6 text-center text-[#75807A] text-sm">Chargement…</div>}
          {!loading && msgs.length === 0 && <div className="p-8 text-center text-[#75807A] text-sm">Aucun message pour l'instant.</div>}
          {msgs.map((m) => (
            <button key={m.id} data-testid={`message-item-${m.id}`} onClick={() => open(m)}
              className="w-full text-left px-4 py-3 border-b border-[#F2EEE7] hover:bg-[#FAF7F1] transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm ${m.read ? "font-medium text-[#3B423E]" : "font-bold text-[#1A1A1A]"}`}>
                  {!m.read && <span className="inline-block w-2 h-2 rounded-full bg-[#F26419] mr-1.5 align-middle" />}{m.name}
                </span>
                <span className="text-[11px] text-[#75807A] shrink-0">{fmtDate(m.created_at)}</span>
              </div>
              <div className="truncate text-sm text-[#5A635D] mt-0.5">{m.message}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Écrire (envoi brandé + pièce jointe) ---------------- */
function ComposeTab({ status, prefill }) {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [attachment, setAttachment] = useState(null); // {url, name}
  const [attaching, setAttaching] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => { if (prefill?.to) setForm((f) => ({ ...f, to: prefill.to })); }, [prefill?.ts]);

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/mail/preview", { body: form.body, attachment_name: attachment?.name || "" });
      setPreview(r.data.html);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPreviewing(false); }
  };

  if (status && !status.configured) {
    return (
      <div data-testid="mail-not-configured" className="rounded-2xl border border-[#E2DFD8] bg-white p-6 max-w-2xl">
        <div className="flex items-center gap-2 text-[#D94F2B] font-bold"><AlertTriangle className="w-5 h-5" /> Service email non configuré</div>
        <p className="mt-3 text-[#3B423E]">Le service d'envoi n'est pas encore activé.</p>
      </div>
    );
  }

  const attach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/mail/attachment", fd);
      setAttachment({ url: r.data.url, name: r.data.name });
      toast.success("Pièce jointe ajoutée");
    } catch (err) { toast.error(apiError(err)); }
    finally { setAttaching(false); e.target.value = ""; }
  };

  const send = async () => {
    if (!form.to.trim() || !form.body.trim()) { toast.error("Destinataire et message requis"); return; }
    setSending(true);
    try {
      await api.post("/admin/mail/send", {
        ...form,
        attachment_url: attachment?.url || "",
        attachment_name: attachment?.name || "",
      });
      toast.success("Email envoyé ✅");
      setForm({ to: "", subject: "", body: "" });
      setAttachment(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl rounded-2xl border border-[#E2DFD8] bg-white p-4 sm:p-5">
      <h3 className="font-head font-bold mb-3">Nouveau message</h3>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-[#75807A]">Modèles :</span>
        {REPLY_TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            data-testid={`mail-template-${tpl.id}`}
            onClick={() => setForm((f) => ({ ...f, subject: f.subject || tpl.subject, body: tpl.body }))}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#F26419]/40 bg-[#FEF4EC] text-[#D94F2B] text-xs font-bold hover:bg-[#FDE7D8] transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" /> {tpl.label}
          </button>
        ))}
      </div>
      <Input data-testid="mail-new-to" placeholder="Email du client" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="mb-3" />
      <Input data-testid="mail-new-subject" placeholder="Objet" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mb-3" />
      <textarea data-testid="mail-new-body" rows={9} placeholder="Votre message…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full rounded-xl border-2 border-[#E2DFD8] px-4 py-3 focus:outline-none focus:border-[#F26419]" />

      {/* Pièce jointe */}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <label data-testid="mail-attach-label" className="cursor-pointer inline-flex items-center gap-2 h-10 px-4 rounded-xl border-2 border-[#E2DFD8] hover:border-[#F26419] text-sm font-semibold text-[#3B423E]">
          <Paperclip className="w-4 h-4" /> {attaching ? "Ajout…" : "Joindre un fichier"}
          <input data-testid="mail-attach-input" type="file" className="hidden" onChange={attach} disabled={attaching} />
        </label>
        {attachment && (
          <span data-testid="mail-attachment-chip" className="inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-[#FEF1E7] text-[#D94F2B] text-sm font-semibold">
            <FileText className="w-4 h-4" /> {attachment.name}
            <button onClick={() => setAttachment(null)} className="hover:text-[#B0392B]"><Trash2 className="w-3.5 h-3.5" /></button>
          </span>
        )}
      </div>

      <p className="mt-3 text-xs text-[#75807A]">Logo {brandName()}, couleur orange et signature ajoutés automatiquement. La pièce jointe est envoyée sous forme de lien de téléchargement sécurisé. Réponses dirigées vers {contactEmail()}.</p>
      <div className="mt-3 flex items-center gap-2">
        <Button data-testid="mail-preview-btn" variant="outline" onClick={openPreview} disabled={previewing || !form.body.trim()} className="gap-2">
          <Eye className="w-4 h-4" /> {previewing ? "Aperçu…" : "Aperçu"}
        </Button>
        <Button data-testid="mail-new-send" onClick={send} disabled={sending} className="gap-2 bg-[#F26419] hover:bg-[#D85312] text-white">
          <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer"}
        </Button>
      </div>

      <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="mail-preview-modal" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#F26419]" /> Aperçu de l'email</DialogTitle>
            <DialogDescription>Rendu final tel que le client le recevra.</DialogDescription>
          </DialogHeader>
          <iframe data-testid="mail-preview-frame" title="Aperçu email" srcDoc={preview || ""} className="w-full h-[520px] rounded-xl border border-[#E2DFD8] bg-[#FDF8F0]" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Documents (dossiers) ---------------- */
function DocumentsTab() {
  const [folders, setFolders] = useState([]);
  const [active, setActive] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [uploading, setUploading] = useState(false);

  const loadFolders = async () => {
    try { const r = await api.get("/admin/documents/folders"); setFolders(r.data.folders || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadFolders(); }, []);

  const openFolder = async (f) => {
    setActive(f); setLoading(true); setFiles([]);
    try { const r = await api.get(`/admin/documents?folder_id=${encodeURIComponent(f.id)}`); setFiles(r.data.files || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    try { await api.post("/admin/documents/folders", { name: newFolder }); setNewFolder(""); loadFolders(); toast.success("Dossier créé"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Supprimer le dossier « ${f.name} » et son contenu ?`)) return;
    try { await api.delete(`/admin/documents/folders/${f.id}`); if (active?.id === f.id) setActive(null); loadFolders(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const renameFolder = async (f) => {
    const name = window.prompt("Nouveau nom du dossier :", f.name);
    if (!name || !name.trim() || name.trim() === f.name) return;
    try { await api.patch(`/admin/documents/folders/${f.id}`, { name: name.trim() }); loadFolders(); toast.success("Dossier renommé"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("folder_id", active.id);
      fd.append("file", file);
      await api.post("/admin/documents/upload", fd);
      toast.success("Fichier ajouté ✅");
      openFolder(active); loadFolders();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (f) => {
    try {
      const res = await api.get(f.download_path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.data.type || f.content_type || "application/octet-stream" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Supprimer « ${f.name} » ?`)) return;
    try { await api.delete(`/admin/documents/${f.id}`); openFolder(active); loadFolders(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (active) {
    return (
      <div data-testid="documents-folder-view">
        <button onClick={() => setActive(null)} className="text-sm text-[#75807A] flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Tous les dossiers</button>
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="font-head font-bold text-lg flex items-center gap-2"><FolderClosed className="w-5 h-5 text-[#F26419]" /> {active.name}
            {active.system && <span className="text-xs bg-[#FEF1E7] text-[#D94F2B] px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> Auto</span>}
          </h3>
          {!active.system && (
            <label data-testid="doc-upload-label" className="cursor-pointer inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm">
              <Upload className="w-4 h-4" /> {uploading ? "Envoi…" : "Ajouter un fichier"}
              <input data-testid="doc-upload-input" type="file" className="hidden" onChange={upload} disabled={uploading} />
            </label>
          )}
        </div>
        {active.system && <p className="text-sm text-[#75807A] mb-3">Les contrats signés sont classés automatiquement ici.</p>}
        <div className="rounded-2xl border border-[#E2DFD8] bg-white overflow-hidden">
          {loading && <div className="p-6 text-center text-[#75807A] text-sm">Chargement…</div>}
          {!loading && files.length === 0 && <div className="p-8 text-center text-[#75807A] text-sm">Dossier vide.</div>}
          {files.map((f) => (
            <div key={f.id} data-testid={`doc-file-${f.id}`} className="px-4 py-3 border-b border-[#F2EEE7] flex items-center gap-3 hover:bg-[#FAF7F1]">
              <FileText className="w-5 h-5 text-[#F26419] shrink-0" />
              <button onClick={() => download(f)} className="flex-1 text-left min-w-0">
                <div className="truncate text-sm font-semibold text-[#1A1A1A]">{f.name}</div>
                <div className="text-xs text-[#75807A]">{fmtDate(f.created_at)}{f.size ? ` · ${(f.size / 1024).toFixed(0)} Ko` : ""}</div>
              </button>
              {!f.system && (
                <button data-testid={`doc-delete-${f.id}`} onClick={() => deleteFile(f)} className="text-[#B0392B] hover:bg-[#FBE9E7] p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="documents-folders">
      <div className="flex items-center gap-2 mb-4 max-w-md">
        <Input data-testid="new-folder-name" placeholder="Nouveau dossier…" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} />
        <Button data-testid="create-folder-btn" onClick={createFolder} className="gap-2 bg-[#1A1A1A] hover:bg-black text-white shrink-0"><FolderPlus className="w-4 h-4" /> Créer</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {folders.map((f) => (
          <div key={f.id} data-testid={`folder-${f.id}`} className="group relative rounded-2xl border border-[#E2DFD8] bg-white p-4 hover:border-[#F26419] transition-colors">
            <button onClick={() => openFolder(f)} className="w-full text-left">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#FEF1E7] flex items-center justify-center"><FolderClosed className="w-5 h-5 text-[#F26419]" /></span>
                <div className="min-w-0">
                  <div className="font-bold text-[#1A1A1A] truncate flex items-center gap-1.5">{f.name}{f.system && <Lock className="w-3 h-3 text-[#75807A]" />}</div>
                  <div className="text-xs text-[#75807A]">{f.count} fichier{f.count > 1 ? "s" : ""}</div>
                </div>
              </div>
            </button>
            {!f.system && (
              <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`folder-rename-${f.id}`} onClick={() => renameFolder(f)} className="text-[#75807A] hover:bg-[#F2EDE4] p-1.5 rounded-lg"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`folder-delete-${f.id}`} onClick={() => deleteFolder(f)} className="text-[#B0392B] hover:bg-[#FBE9E7] p-1.5 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
